import { useState, useEffect } from 'react';
import { message } from 'antd';
import {
  adminService,
  PromotionPackage,
  CreatePromotionPackageRequest,
  UpdatePromotionPackageRequest,
} from '../../api/services/adminService';

export const usePromotionPackages = () => {
  const [packages, setPackages] = useState<PromotionPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PromotionPackage | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const result = await adminService.getPromotionPackages();
      setPackages(Array.isArray(result) ? result : []);
    } catch {
      message.error('Failed to load promotion packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: CreatePromotionPackageRequest) => {
    setSubmitting(true);
    try {
      await adminService.createPromotionPackage(values);
      message.success('Promotion package created');
      setModalVisible(false);
      fetchPackages();
    } catch {
      message.error('Failed to create promotion package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string, values: UpdatePromotionPackageRequest) => {
    setSubmitting(true);
    try {
      await adminService.updatePromotionPackage(id, values);
      message.success('Promotion package updated');
      setModalVisible(false);
      fetchPackages();
    } catch {
      message.error('Failed to update promotion package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminService.deletePromotionPackage(id);
      message.success('Promotion package deleted');
      fetchPackages();
    } catch {
      message.error('Failed to delete promotion package');
    }
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setModalVisible(true);
  };

  const openEditModal = (pkg: PromotionPackage) => {
    setEditingPackage(pkg);
    setModalVisible(true);
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  return {
    packages,
    loading,
    modalVisible,
    editingPackage,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch: fetchPackages,
  };
};
